// Page-scoped Clerk provider boundary. _app.tsx does not mount ClerkProvider:
// a global provider kept the whole @clerk/nextjs client SDK (~48 kB gzip) in
// the shared _app chunk that every page loads, which failed the shell bundle
// budget (scripts/oracle2/bundle-budget.mjs). Each surface that renders Clerk
// UI or calls Clerk hooks mounts this boundary itself instead:
//
//   - /sso-callback and /account/sign-in-methods import it statically. Those
//     route chunks carry Clerk only for the members who visit them.
//   - The dashboard login wall reaches it through a next/dynamic import of
//     the sign-in panel (TwtSignInPanelWithProvider), so Clerk stays out of
//     the budgeted /dashboard route chunk too and loads as its own chunk.
//
// Without the publishable key the boundary renders its `fallback` (nothing
// unless the surface passes one), so children that call Clerk hooks
// (useSignIn, useUser, AuthenticateWithRedirectCallback) never mount outside
// a provider — including at build-time prerender, where an unconditional
// mount would fail `next build` on a deployment without Clerk keys.
// NEXT_PUBLIC_* is inlined at build time. Without Clerk keys, the sign-in
// panel keeps its email form visible and reports that sign-in is unavailable.
import type React from "react";
import { ClerkProvider as ClerkProviderBase } from "@clerk/nextjs";

// @clerk/nextjs types ClerkProvider as the App Router async server component,
// but the shipped component is a compatibility layer that also supports the
// Pages Router (dist client-boundary/ClerkProvider: "a single ClerkProvider
// component in both the app and pages routers"). Retype it for this router.
type Localization = Parameters<typeof ClerkProviderBase>[0]["localization"];

const ClerkProvider = ClerkProviderBase as unknown as (props: {
  publishableKey: string;
  localization?: Localization;
  prefetchUI?: boolean;
  children: React.ReactNode;
}) => React.JSX.Element;

export const clerkPublishableKey =
  process.env.NEXT_PUBLIC_CLERK_PUBLISHABLE_KEY?.trim() ?? "";

export function ClerkProviderBoundary({
  localization,
  prefetchUI,
  fallback = null,
  children,
}: {
  /** Wording overrides for Clerk's own components on this surface. Clerk
   * keeps one instance per page, so every boundary on a page should pass
   * the same value. */
  localization?: Localization;
  /** Custom flows can skip Clerk's prebuilt UI script. */
  prefetchUI?: boolean;
  /** What the surface shows without the publishable key. Must not call
   * Clerk hooks. Nothing unless set. */
  fallback?: React.ReactNode;
  children: React.ReactNode;
}): React.JSX.Element | null {
  if (!clerkPublishableKey) return <>{fallback}</>;
  return (
    <ClerkProvider
      publishableKey={clerkPublishableKey}
      localization={localization}
      prefetchUI={prefetchUI}
    >
      {children}
    </ClerkProvider>
  );
}
